
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Droplets, Leaf, Recycle, FlaskConical,
  ChevronDown, MapPin, Clock, Users, GraduationCap,
  ArrowRight, CheckCircle
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const trainingCategories = [
  {
    id: 'wash',
    icon: Droplets,
    title: 'WASH Trainings',
    subtitle: 'Water, Sanitation & Hygiene',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    courses: [
      {
        title: 'Sanitary Installation and Hygiene',
        topics: [
          'Construction of odourless latrines',
          'Solar water heater installation',
          'Filtration systems setup',
          'Water analysis and treatment',
          'Production of disinfectants'
        ],
        duration: '2-4 weeks',
        target: 'Community health workers, plumbers, entrepreneurs'
      },
      {
        title: 'Hand-Drilled Boreholes',
        topics: [
          'Manual drilling techniques',
          'Geological assessment',
          'Pump installation',
          'Well maintenance',
          'Water quality testing'
        ],
        duration: '3-6 weeks',
        target: 'Technicians, community leaders, NGO workers'
      },
      {
        title: 'Rainwater Harvesting Systems',
        topics: [
          'Catchment design',
          'Storage tank construction',
          'First-flush systems',
          'Water treatment',
          'System maintenance'
        ],
        duration: '1-2 weeks',
        target: 'Homeowners, builders, community groups'
      }
    ]
  },
  {
    id: 'agriculture',
    icon: Leaf,
    title: 'Agriculture Trainings',
    subtitle: 'Sustainable Farming Practices',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    courses: [
      {
        title: 'Mushroom Farming',
        topics: [
          'Substrate preparation',
          'Spawn production',
          'Growing conditions',
          'Harvesting techniques',
          'Marketing and sales'
        ],
        duration: '2-3 weeks',
        target: 'Farmers, entrepreneurs, youth groups'
      },
      {
        title: 'Bee Keeping',
        topics: [
          'Hive construction',
          'Colony management',
          'Honey harvesting',
          'Wax processing',
          'Product marketing'
        ],
        duration: '2-3 weeks',
        target: 'Farmers, rural communities, cooperatives'
      },
      {
        title: 'Snail Farming (Heliculture)',
        topics: [
          'Snailery construction',
          'Species selection',
          'Feeding management',
          'Breeding techniques',
          'Processing and marketing'
        ],
        duration: '1-2 weeks',
        target: 'Entrepreneurs, farmers, women groups'
      },
      {
        title: 'Poultry Farming',
        topics: [
          'Housing construction',
          'Breed selection',
          'Feed formulation',
          'Disease management',
          'Egg and meat production'
        ],
        duration: '2-4 weeks',
        target: 'Farmers, rural entrepreneurs'
      },
      {
        title: 'Cane Rat (Grasscutter) Farming',
        topics: [
          'Housing design',
          'Breeding management',
          'Nutrition requirements',
          'Health management',
          'Marketing strategies'
        ],
        duration: '1-2 weeks',
        target: 'Wildlife farmers, entrepreneurs'
      },
      {
        title: 'Fish Farming (Aquaculture)',
        topics: [
          'Pond construction',
          'Fish species selection',
          'Water quality management',
          'Feeding practices',
          'Harvesting and processing'
        ],
        duration: '2-4 weeks',
        target: 'Farmers, cooperatives, investors'
      },
      {
        title: 'Palm Weevil Farming',
        topics: [
          'Breeding containers',
          'Lifecycle management',
          'Harvesting techniques',
          'Processing methods',
          'Market development'
        ],
        duration: '1 week',
        target: 'Entrepreneurs, food producers'
      },
      {
        title: 'Food & Vegetable Crops',
        topics: [
          'Crop selection',
          'Soil preparation',
          'Irrigation techniques',
          'Pest management',
          'Harvest and storage'
        ],
        duration: '2-4 weeks',
        target: 'Farmers, gardeners, cooperatives'
      }
    ]
  },
  {
    id: 'waste',
    icon: Recycle,
    title: 'Waste Management & Recycling',
    subtitle: 'Turning Waste into Resources',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    courses: [
      {
        title: 'Reused Waste Technologies',
        topics: [
          'Bricks, tiles & blocks from waste',
          'Clean gasoline production',
          'Cooking oil recycling',
          'Fertilizer from organic waste',
          'Animal feed production',
          'Hydroponic fodder',
          'Maggot farming',
          'Air layering techniques',
          'Humanure processing'
        ],
        duration: '2-4 weeks',
        target: 'Recyclers, entrepreneurs, municipalities'
      },
      {
        title: 'Bio-Digester Systems',
        topics: [
          'Digester construction',
          'Biogas production',
          'Gasoline alternatives',
          'Organic fertilizer processing',
          'System maintenance'
        ],
        duration: '2-3 weeks',
        target: 'Farmers, institutions, communities'
      }
    ]
  },
  {
    id: 'food',
    icon: FlaskConical,
    title: 'Food Science Trainings',
    subtitle: 'Processing, Safety & Nutrition',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    courses: [
      {
        title: 'Solar Dryer Construction',
        topics: [
          'Dryer design principles',
          'Material selection',
          'Construction techniques',
          'Temperature management',
          'Product quality control'
        ],
        duration: '1-2 weeks',
        target: 'Farmers, food processors'
      },
      {
        title: 'Food Preservation & Packaging',
        topics: [
          'Preservation methods',
          'Packaging materials',
          'Shelf-life extension',
          'Quality standards',
          'Labeling requirements'
        ],
        duration: '1-2 weeks',
        target: 'Food businesses, cooperatives'
      },
      {
        title: 'Food Processing',
        topics: [
          'Processing equipment',
          'Value addition techniques',
          'Product development',
          'Quality assurance',
          'Market preparation'
        ],
        duration: '2-3 weeks',
        target: 'Food entrepreneurs, processors'
      },
      {
        title: 'Food Microbiology & Safety',
        topics: [
          'Microbial hazards',
          'HACCP principles',
          'Hygiene practices',
          'Testing methods',
          'Regulatory compliance'
        ],
        duration: '2-3 weeks',
        target: 'Food handlers, quality controllers'
      },
      {
        title: 'Nutrition & Future Food Sustainability',
        topics: [
          'Nutritional science basics',
          'Sustainable food systems',
          'Alternative proteins',
          'Food security planning',
          'Community nutrition programs'
        ],
        duration: '1-2 weeks',
        target: 'Nutritionists, community leaders'
      }
    ]
  }
];

const requirements = [
  'English literacy (ability to read and write)',
  'O-Level or A-Level certificate (for some advanced courses)',
  'Basic numeracy skills',
  'Commitment to complete the training program',
  'Interest in community development'
];

export default function TrainingsPage() {
  const [activeTab, setActiveTab] = useState('wash');

  return (
    <div data-testid="trainings-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-primary" data-testid="trainings-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-semibold text-sm uppercase tracking-wider">Education</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 font-heading">
              Trainings & Courses
            </h1>
            <p className="text-xl text-blue-100">
              Build practical skills for sustainable livelihoods with our specialized training programs in WASH, Agriculture, Waste Management, and Food Sciences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Training Info */}
      <section className="py-12 bg-accent" data-testid="training-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Training Location</p>
                <p className="font-semibold text-slate-900">Buea Centre + Periodic International</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Target Groups</p>
                <p className="font-semibold text-slate-900">Individuals, NGOs, Communities</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Certification</p>
                <p className="font-semibold text-slate-900">SMART Centre Certificate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Training Categories with Tabs */}
      <section className="py-16 md:py-24 bg-white" data-testid="training-categories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading">
              Our Training Programs
            </h2>
            <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              Choose from four specialized training categories designed to meet community needs
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 gap-2 bg-transparent h-auto mb-8">
              {trainingCategories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className={`flex items-center gap-2 p-4 rounded-xl border-2 transition-all data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-white ${category.bgColor}`}
                  data-testid={`tab-${category.id}`}
                >
                  <category.icon className="h-5 w-5" />
                  <span className="font-semibold text-sm">{category.title}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {trainingCategories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`${category.bgColor} p-6 rounded-t-2xl`}>
                    <div className="flex items-center gap-3">
                      <category.icon className={`h-8 w-8 ${category.color}`} />
                      <div>
                        <h3 className="text-2xl font-bold text-slate-900 font-heading">{category.title}</h3>
                        <p className="text-slate-600">{category.subtitle}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-t-0 border-slate-200 rounded-b-2xl p-6">
                    <Accordion type="single" collapsible className="space-y-4">
                      {category.courses.map((course, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`course-${index}`}
                          className="border border-slate-200 rounded-xl overflow-hidden"
                          data-testid={`accordion-${category.id}-${index}`}
                        >
                          <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 [&[data-state=open]]:bg-slate-50">
                            <span className="text-left font-semibold text-slate-900">{course.title}</span>
                          </AccordionTrigger>
                          <AccordionContent className="px-6 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-3">Topics Covered:</h4>
                                <ul className="space-y-2">
                                  {course.topics.map((topic) => (
                                    <li key={topic} className="flex items-start gap-2 text-sm text-slate-600">
                                      <CheckCircle className="h-4 w-4 text-secondary flex-shrink-0 mt-0.5" />
                                      {topic}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                  <Clock className="h-5 w-5 text-primary" />
                                  <div>
                                    <p className="text-sm text-slate-500">Duration</p>
                                    <p className="font-semibold text-slate-900">{course.duration}</p>
                                  </div>
                                </div>
                                <div className="flex items-start gap-3">
                                  <Users className="h-5 w-5 text-primary flex-shrink-0" />
                                  <div>
                                    <p className="text-sm text-slate-500">Target Audience</p>
                                    <p className="font-semibold text-slate-900">{course.target}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </motion.div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Entry Requirements */}
      <section className="py-16 md:py-20 bg-accent" data-testid="requirements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 font-heading">
              Entry Requirements
            </h2>
            <div className="bg-white p-8 rounded-2xl shadow-md">
              <ul className="space-y-4 text-left">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-secondary flex-shrink-0" />
                    <span className="text-slate-700">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Enrollment */}
      <section className="py-16 md:py-20 bg-secondary" data-testid="enrollment-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">
            Ready to Start Your Training?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Enroll now and gain the skills to make a difference in your community. Contact us for course schedules and pricing.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-secondary hover:bg-green-50 rounded-full px-8 py-4 font-semibold text-lg transition-all shadow-lg"
            data-testid="trainings-enroll-btn"
          >
            Register Now <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
